/**
 * Pagination utilities for handling large result sets
 */

export interface PaginationParams {
  page: number;
  pageSize: number;
}

export interface PaginatedResult<T> {
  data: T[];
  metadata: {
    currentPage: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

/**
 * Creates pagination parameters from page and page size
 * @param page - The page number (1-indexed)
 * @param pageSize - The number of items per page
 * @returns Pagination parameters
 */
export function createPaginationParams(page: number = 1, pageSize: number = 10): PaginationParams {
  return {
    page: Math.max(1, page), // Ensure page is at least 1
    pageSize: Math.max(1, Math.min(100, pageSize)), // Ensure pageSize is between 1 and 100
  };
}

/**
 * Calculates pagination metadata
 * @param totalItems - The total number of items
 * @param params - Pagination parameters
 * @returns Pagination metadata
 */
export function calculatePaginationMetadata(
  totalItems: number,
  params: PaginationParams
) {
  const { page, pageSize } = params;
  const totalPages = Math.ceil(totalItems / pageSize);
  
  return {
    currentPage: page,
    pageSize,
    totalItems,
    totalPages,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,
  };
}

/**
 * Creates a paginated result
 * @param data - The data to paginate
 * @param totalItems - The total number of items
 * @param params - Pagination parameters
 * @returns Paginated result
 */
export function createPaginatedResult<T>(
  data: T[],
  totalItems: number,
  params: PaginationParams
): PaginatedResult<T> {
  return {
    data,
    metadata: calculatePaginationMetadata(totalItems, params),
  };
}

/**
 * Calculates the range for a SQL query
 * @param params - Pagination parameters
 * @returns Range object with from and to values
 */
export function calculateRange(params: PaginationParams) {
  const { page, pageSize } = params;
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  
  return { from, to };
} 